import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '../Services/user.service';
import { SharedService } from '../Services/sharedService';
import { AuthService } from '../Services/auth.service';
import { ActivationDestinationService } from '../onboarding-checklist/activation-destination.service';
import { ActivationJourneyService } from '../onboarding-checklist/activation-journey.service';

@Component({
  selector: 'app-on-boarding',
  templateUrl: './on-boarding.component.html',
  styleUrl: './on-boarding.component.css',
  standalone: false
})
export class OnBoardingComponent implements OnInit {
  workspaceForm: FormGroup;
  isShowLoading = false;
  userInformation: any;
  private exiting = false;
  private hasShownForm = false;

  constructor(
    private fb: FormBuilder,
    private _userService: UserService,
    private _sharedService: SharedService,
    private _authService: AuthService,
    private router: Router,
    private activationDestination: ActivationDestinationService,
    private activationJourney: ActivationJourneyService
  ) {
    this.workspaceForm = this.fb.group({
      company_name: [''],
    });
  }

  ngOnInit(): void {
    this.activationJourney.trackEvent('workspace_prompt_shown');
    this._sharedService.isOpenCloseSidebar.next(false);
    const existing = this._userService.getCurrentUserValue();
    if (existing) {
      this.applyUser(existing);
    } else {
      this._userService.fetchCurrentUser().subscribe((user) => {
        if (user) {
          this.applyUser(user);
        }
      });
    }
    this._userService.currentUser$.subscribe((user) => {
      if (user && !this.exiting) {
        this.applyUser(user);
      }
    });
  }

  continueWorkspace(): void {
    this.completePrompt(false);
  }

  skipWorkspace(): void {
    this.completePrompt(true);
  }

  openSupportChat(): void {
    window.open(
      'https://api.whatsapp.com/send/?phone=916354719703&text=Hi%2C+I+need+help+in+setting+up+SuperSee.&type=phone_number&app_absent=0',
      '_blank'
    );
  }

  logoutUser(): void {
    this._authService.performLogout();
  }

  private applyUser(user: any): void {
    this.userInformation = user;
    const existingName = (user?.meta?.company_name || '').toString();
    if (!this.workspaceForm.get('company_name')?.dirty) {
      this.workspaceForm.patchValue({ company_name: existingName });
    }
    if (this._sharedService.checkingOnBoarding(user)) {
      if (!this.hasShownForm) {
        this.exitAfterPrompt(true);
      }
      return;
    }
    this.hasShownForm = true;
  }

  private completePrompt(skip: boolean): void {
    if (this.isShowLoading || this.exiting) {
      return;
    }
    const trimmed = String(this.workspaceForm.value.company_name || '').trim();
    const meta: Record<string, string> = {
      ss_workspace_prompt_completed: '1',
    };
    if (!skip && trimmed) {
      meta.company_name = trimmed;
    }
    this.isShowLoading = true;
    this.activationJourney.trackEvent(skip ? 'workspace_prompt_skipped' : 'workspace_prompt_continued');
    this._userService.updateUser({ meta }).subscribe({
      next: () => {
        this.patchUserMeta(meta);
        this.exitAfterPrompt(false);
      },
      error: () => {
        this.patchUserMeta(meta);
        this.exitAfterPrompt(false);
      },
    });
  }

  private patchUserMeta(meta: Record<string, string>): void {
    if (!this.userInformation) {
      this.userInformation = { meta };
    } else {
      this.userInformation = {
        ...this.userInformation,
        meta: {
          ...(this.userInformation.meta || {}),
          ...meta,
        },
      };
    }
    this._userService.setCurrentUser(this.userInformation);
    this._userService.fetchCurrentUser().subscribe((fresh) => {
      if (!fresh) {
        return;
      }
      const merged = {
        ...fresh,
        meta: {
          ...(fresh.meta || {}),
          ...meta,
        },
      };
      this.userInformation = merged;
      this._userService.setCurrentUser(merged);
    });
  }

  private exitAfterPrompt(replaceUrl: boolean): void {
    if (this.exiting) {
      return;
    }
    this.exiting = true;
    this.isShowLoading = false;
    this.activationDestination.postLoginDestination().subscribe((dest) => {
      void this.router.navigate([dest.route], {
        queryParams: dest.queryParams || {},
        replaceUrl,
      });
    });
  }
}
